import cx from 'classnames';
import { useId, useState } from 'react';
import { useImperativeTimeout } from '../hooks/useImperativeTimeout.ts';
import globalStyles from '../index.module.scss';
import { downloadBlob, PNG_CONTENT_TYPE } from '../lib/file';
import { rasterizeSvg } from '../lib/image';
import {
  ClipboardIcon,
  DownloadIcon,
  ExternalLinkIcon,
  PrinterIcon,
  SaveIcon,
  ShareIcon,
  XIcon,
} from './FretboardPanel/icons.tsx';
import { MenuButton } from './MenuButton';
import styles from './SaveMenu.module.scss';

const PNG_EXPORT_SCALE_SD = 2 as const;
const PNG_EXPORT_SCALE_HD = 4 as const;
const TOAST_DURATION_MS = 3000;

// Whether the browser supports writing a given content type to the clipboard.
// Evaluated lazily so test mocks that stub ClipboardItem are picked up.
function canCopyToClipboard(contentType: string) {
  if (typeof ClipboardItem === 'undefined') return false;
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- clipboard can be undefined in insecure contexts
  if (typeof navigator.clipboard?.write !== 'function') return false;
  // Firefox exposes ClipboardItem but only supports text types, not images.
  // ClipboardItem.supports() detects this; when unavailable (older browsers
  // that do support the type) we fall through optimistically.
  if (typeof ClipboardItem.supports === 'function') {
    return ClipboardItem.supports(contentType);
  }
  return true;
}

// The SVG_CONTENT_TYPE includes ";charset=utf-8" which ClipboardItem doesn't
// accept — use the base MIME type for clipboard operations.
const SVG_CLIPBOARD_TYPE = 'image/svg+xml';

// Whether the browser supports sharing files of a given content type via the
// Web Share API. Uses navigator.canShare() with a dummy file to probe support.
function canShareFile(contentType: string, extension: string) {
  if (typeof navigator.share !== 'function') return false;
  if (typeof navigator.canShare !== 'function') return false;
  const testFile = new File([], `test.${extension}`, { type: contentType });
  return navigator.canShare({ files: [testFile] });
}

async function downloadSvgAsPng(
  svgUrl: string,
  filenameBase: string,
  width: number,
  height: number,
) {
  const pngBlob = await rasterizeSvg(svgUrl, PNG_CONTENT_TYPE, width, height);
  downloadBlob(pngBlob, `${filenameBase}.png`);
}

// Pass the rasterization Promise directly to ClipboardItem so the
// clipboard.write call happens synchronously within the user gesture —
// if we await the rasterization first, mobile browsers reject the write
// because the gesture has expired by the time we reach clipboard.write.
function copySvgToClipboardPng(svgUrl: string, width: number, height: number) {
  return navigator.clipboard.write([
    new ClipboardItem({
      [PNG_CONTENT_TYPE]: rasterizeSvg(svgUrl, PNG_CONTENT_TYPE, width, height),
    }),
  ]);
}

// @todo Consider using dataUrl here?
// Has drawbacks (too long, might hit limits) but also benefits (no need to revoke object URL, can
// be bookmarked, the URL is not revoked so it survives browser refresh, etc.)
async function openInNewTabSvgAsPng(
  svgUrl: string,
  width: number,
  height: number,
) {
  const pngBlob = await rasterizeSvg(svgUrl, PNG_CONTENT_TYPE, width, height);
  const pngUrl = URL.createObjectURL(pngBlob);
  window.open(pngUrl, '_blank');
  URL.revokeObjectURL(pngUrl);
}

type Toast = { key: number; message: string; type: 'success' | 'error' };
let toastKey = 0;

type SaveMenuProps = {
  svgUrl: string;
  filenameBase: string;
  instrumentName: string;
  tuningName: string;
  tuning: readonly string[];
  patternName: string;
  rootNote: string;
  width: number;
  height: number;
  /** Triggers a print that shows only this panel. */
  onPrintSolo: () => void;
};

export function SaveMenu(props: SaveMenuProps) {
  const svgMenuGroupLabelId = useId();
  const pngMenuGroupLabelId = useId();
  const [toast, setToast] = useState<Toast | null>(null);
  const toastTimeout = useImperativeTimeout();

  function showToast(message: string, type: Toast['type']) {
    setToast({ key: ++toastKey, message, type });
    toastTimeout.schedule(() => {
      setToast(null);
    }, TOAST_DURATION_MS);
  }

  async function handleCopySvg() {
    try {
      const blob = await fetch(props.svgUrl).then((r) => r.blob());
      await navigator.clipboard.write([
        new ClipboardItem({
          [SVG_CLIPBOARD_TYPE]: new Blob([blob], { type: SVG_CLIPBOARD_TYPE }),
        }),
      ]);
      showToast('Copied!', 'success');
    } catch (error) {
      console.error('SVG clipboard copy failed:', error);
      showToast('Failed to copy', 'error');
    }
  }

  // SVG share handler — disabled (see comment in JSX) but kept for future use.
  // async function handleShareSvg() {
  //   try {
  //     const blob = await fetch(props.svgUrl).then((r) => r.blob());
  //     const file = new File([blob], `${props.filenameBase}.svg`, {
  //       type: SVG_CLIPBOARD_TYPE,
  //     });
  //     await navigator.share({ files: [file] });
  //   } catch (error) {
  //     if (error instanceof DOMException && error.name === 'AbortError') return;
  //     console.error('SVG share failed:', error);
  //     showToast('Failed to share', 'error');
  //   }
  // }

  const shareText = `${props.instrumentName} ${props.tuningName} (${props.tuning.join(' ')})\n${props.patternName} (root ${props.rootNote})\n\nMade with ${import.meta.env.PACKAGE_HOMEPAGE}`;

  async function handleSharePng(width: number, height: number, suffix: string) {
    try {
      const pngBlob = await rasterizeSvg(
        props.svgUrl,
        PNG_CONTENT_TYPE,
        width,
        height,
      );
      const file = new File([pngBlob], `${props.filenameBase}-${suffix}.png`, {
        type: PNG_CONTENT_TYPE,
      });
      await navigator.share({
        text: shareText,
        files: [file],
      });
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      console.error('PNG share failed:', error);
      showToast('Failed to share', 'error');
    }
  }

  async function handleCopy(width: number, height: number) {
    try {
      await copySvgToClipboardPng(props.svgUrl, width, height);
      showToast('Copied!', 'success');
    } catch (error) {
      console.error('Clipboard copy failed:', error);
      showToast('Failed to copy', 'error');
    }
  }

  return (
    <>
      {toast && (
        <div
          key={toast.key}
          className={cx(styles.toast, {
            [styles.toastError]: toast.type === 'error',
          })}
          role="status"
        >
          {toast.message}
        </div>
      )}

      <MenuButton
        ariaLabel="Download options"
        transitionMs={220}
        className={styles.downloadMenu}
        renderMenu={({ closeMenu, menuItemClassName }) => (
          <>
            <button
              type="button"
              role="menuitem"
              className={cx(globalStyles.linkButton, menuItemClassName)}
              onClick={() => {
                closeMenu();
                props.onPrintSolo();
              }}
            >
              <PrinterIcon /> Print
            </button>

            <div
              role="group"
              aria-labelledby={svgMenuGroupLabelId}
              className={styles.menuSection}
            >
              <div
                id={svgMenuGroupLabelId}
                className={styles.menuSectionTitle}
              >
                .SVG
              </div>

              <a
                role="menuitem"
                href={props.svgUrl}
                target="_blank"
                className={cx(globalStyles.linkButton, menuItemClassName)}
                onClick={closeMenu}
              >
                <ExternalLinkIcon /> <span>Open in new tab</span>
              </a>

              <a
                role="menuitem"
                href={props.svgUrl}
                download={`${props.filenameBase}.svg`}
                className={cx(globalStyles.linkButton, menuItemClassName)}
                onClick={closeMenu}
              >
                <DownloadIcon /> <span>Download</span>
              </a>

              {/* SVG share is disabled — most share targets (messaging apps,
                  social media) don't render SVG, so the shared file is
                  effectively useless for non-technical recipients. */}

              {canCopyToClipboard(SVG_CLIPBOARD_TYPE) && (
                <button
                  type="button"
                  role="menuitem"
                  className={cx(globalStyles.linkButton, menuItemClassName)}
                  onClick={() => {
                    void handleCopySvg();
                    closeMenu();
                  }}
                >
                  <ClipboardIcon /> <span>Copy to clipboard</span>
                </button>
              )}
            </div>

            <div
              role="group"
              aria-labelledby={pngMenuGroupLabelId}
              className={styles.menuSection}
            >
              <div
                id={pngMenuGroupLabelId}
                className={styles.menuSectionTitle}
              >
                .PNG
              </div>

              <button
                type="button"
                role="menuitem"
                className={cx(globalStyles.linkButton, menuItemClassName)}
                onClick={() => {
                  closeMenu();

                  void openInNewTabSvgAsPng(
                    props.svgUrl,
                    props.width * PNG_EXPORT_SCALE_SD,
                    props.height * PNG_EXPORT_SCALE_SD,
                  );
                }}
              >
                <ExternalLinkIcon />{' '}
                <span>
                  Open in new tab <small>(SD)</small>
                </span>
              </button>

              <button
                type="button"
                role="menuitem"
                className={cx(globalStyles.linkButton, menuItemClassName)}
                onClick={() => {
                  closeMenu();

                  void openInNewTabSvgAsPng(
                    props.svgUrl,
                    props.width * PNG_EXPORT_SCALE_HD,
                    props.height * PNG_EXPORT_SCALE_HD,
                  );
                }}
              >
                <ExternalLinkIcon />{' '}
                <span>
                  Open in new tab <small>(HD)</small>
                </span>
              </button>

              <button
                type="button"
                role="menuitem"
                className={cx(globalStyles.linkButton, menuItemClassName)}
                onClick={() => {
                  closeMenu();

                  void downloadSvgAsPng(
                    props.svgUrl,
                    `${props.filenameBase}-SD`,
                    props.width * PNG_EXPORT_SCALE_SD,
                    props.height * PNG_EXPORT_SCALE_SD,
                  );
                }}
              >
                <DownloadIcon />{' '}
                <span>
                  Download <small>(SD)</small>
                </span>
              </button>

              <button
                type="button"
                role="menuitem"
                className={cx(globalStyles.linkButton, menuItemClassName)}
                onClick={() => {
                  closeMenu();

                  void downloadSvgAsPng(
                    props.svgUrl,
                    `${props.filenameBase}-HD`,
                    props.width * PNG_EXPORT_SCALE_HD,
                    props.height * PNG_EXPORT_SCALE_HD,
                  );
                }}
              >
                <DownloadIcon />{' '}
                <span>
                  Download <small>(HD)</small>
                </span>
              </button>

              {canShareFile(PNG_CONTENT_TYPE, 'png') && (
                <button
                  type="button"
                  role="menuitem"
                  className={cx(globalStyles.linkButton, menuItemClassName)}
                  onClick={() => {
                    void handleSharePng(
                      props.width * PNG_EXPORT_SCALE_SD,
                      props.height * PNG_EXPORT_SCALE_SD,
                      'SD',
                    );
                    closeMenu();
                  }}
                >
                  <ShareIcon />{' '}
                  <span>
                    Share <small>(SD)</small>
                  </span>
                </button>
              )}

              {canShareFile(PNG_CONTENT_TYPE, 'png') && (
                <button
                  type="button"
                  role="menuitem"
                  className={cx(globalStyles.linkButton, menuItemClassName)}
                  onClick={() => {
                    void handleSharePng(
                      props.width * PNG_EXPORT_SCALE_HD,
                      props.height * PNG_EXPORT_SCALE_HD,
                      'HD',
                    );
                    closeMenu();
                  }}
                >
                  <ShareIcon />{' '}
                  <span>
                    Share <small>(HD)</small>
                  </span>
                </button>
              )}

              {canCopyToClipboard(PNG_CONTENT_TYPE) && (
                <button
                  type="button"
                  role="menuitem"
                  className={cx(globalStyles.linkButton, menuItemClassName)}
                  onClick={() => {
                    // Start the clipboard write BEFORE closing the menu —
                    // some mobile browsers invalidate the user gesture if a
                    // focus/DOM change (from closeMenu) happens first.
                    void handleCopy(
                      props.width * PNG_EXPORT_SCALE_SD,
                      props.height * PNG_EXPORT_SCALE_SD,
                    );
                    closeMenu();
                  }}
                >
                  <ClipboardIcon />{' '}
                  <span>
                    Copy to clipboard <small>(SD)</small>
                  </span>
                </button>
              )}

              {canCopyToClipboard(PNG_CONTENT_TYPE) && (
                <button
                  type="button"
                  role="menuitem"
                  className={cx(globalStyles.linkButton, menuItemClassName)}
                  onClick={() => {
                    void handleCopy(
                      props.width * PNG_EXPORT_SCALE_HD,
                      props.height * PNG_EXPORT_SCALE_HD,
                    );
                    closeMenu();
                  }}
                >
                  <ClipboardIcon />{' '}
                  <span>
                    Copy to clipboard <small>(HD)</small>
                  </span>
                </button>
              )}
            </div>
          </>
        )}
      >
        {({ isOpen }) => (
          <span
            aria-hidden="true"
            className={styles.menuTriggerIcon}
          >
            {isOpen ? <XIcon /> : <SaveIcon />}
          </span>
        )}
      </MenuButton>
    </>
  );
}
